import {
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  TextField,
  Typography
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { t } from "ttag";
import Checkbox from "../../components/cool_widgets/CoolCheckbox";
import Tooltip from "../../components/Tooltip/LightTooltip";
import { IEntityRef } from "../../models/DeviceHealth.types";
import useStyles from "./HealthFilter.style";

interface IHealthFilterProps {
  title: string;
  /**
   * Label for the select-everything row, e.g. "All Site Names". Passed in as a
   * whole phrase rather than built from `title` so the plural reads correctly and
   * translators get a complete string instead of a concatenation.
   */
  allLabel: string;
  options: IEntityRef[];
  /** explicitly selected ids - an empty array means nothing is selected */
  selected: string[];
  onChange: (selected: string[]) => void;
  showSearchBox?: boolean;
  disabled?: boolean;
  disabledText?: string;
}

/**
 * One filter panel: title, optional search box, an "All" toggle and a checkbox
 * list. Ported from the commercial app's Audits filter (which uses MUI v4 +
 * @coolremote/shared) onto the console's MUI v5 + CoolCheckbox stack.
 *
 * The selection is explicit: every checked box is listed in `selected`, so
 * "everything" and "nothing" are distinguishable states. The "All" row is a
 * plain toggle - select every option, or clear the selection.
 */
const HealthFilter: React.FC<IHealthFilterProps> = ({
  title,
  allLabel,
  options,
  selected,
  onChange,
  showSearchBox = false,
  disabled = false,
  disabledText
}) => {
  const classes: any = useStyles();
  const [searchTerm, setSearchTerm] = useState<string>("");

  const allSelected = options.length > 0 && selected.length === options.length;
  const noneSelected = selected.length === 0;

  const visibleOptions = useMemo(() => {
    const sorted = [...options].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    if (!searchTerm) {
      return sorted;
    }

    return sorted.filter(({ name }) => name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [options, searchTerm]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleToggleAll = () => {
    if (allSelected) {
      onChange([]);

      return;
    }

    onChange(options.map(({ id }) => id));
  };

  const handleToggleOption = (optionId: string) => () => {
    if (selected.includes(optionId)) {
      onChange(selected.filter((id) => id !== optionId));

      return;
    }

    onChange([...selected, optionId]);
  };

  const renderSelectionCount = () => {
    // With nothing to choose from, "None" would read as "you cleared this".
    if (!options.length) {
      return null;
    }

    if (allSelected) {
      return <Typography className={classes.selectionCount}>{t`All`}</Typography>;
    }

    if (noneSelected) {
      return <Typography className={classes.selectionCountEmpty}>{t`None`}</Typography>;
    }

    return (
      <Typography className={classes.selectionCount}>
        {selected.length}/{options.length}
      </Typography>
    );
  };

  const renderOptions = () => {
    if (disabled) {
      return (
        <ListItem dense divider>
          <ListItemText className={classes.emptyText} primary={disabledText} />
        </ListItem>
      );
    }

    if (!visibleOptions.length) {
      return (
        <ListItem dense divider>
          <ListItemText className={classes.emptyText} primary={t`No options to display`} />
        </ListItem>
      );
    }

    return (
      <>
        <ListItem dense divider key="all" className={classes.listItem}>
          <ListItemText className={classes.optionText} primary={allLabel} />
          <ListItemSecondaryAction>
            <Checkbox
              edge="end"
              onChange={handleToggleAll}
              checked={allSelected}
              indeterminate={!allSelected && !noneSelected}
              inputProps={{ "aria-label": `toggle all ${title}` }}
            />
          </ListItemSecondaryAction>
        </ListItem>
        {visibleOptions.map(({ id, name }) => (
          <ListItem dense divider key={id} className={classes.listItem}>
            <Tooltip title={name} placement="top">
              <div className={classes.optionTextWrapper}>
                <ListItemText className={classes.optionText} primary={name} />
              </div>
            </Tooltip>
            <ListItemSecondaryAction>
              <Checkbox
                edge="end"
                onChange={handleToggleOption(id)}
                checked={selected.includes(id)}
                inputProps={{ "aria-label": name }}
              />
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </>
    );
  };

  return (
    <Paper elevation={0} className={classes.filterPaper}>
      <div className={classes.filterHeader}>
        <Typography className={classes.filterTitle}>{title}</Typography>
        {!disabled && renderSelectionCount()}
      </div>
      {showSearchBox && !disabled && (
        <TextField
          placeholder={t`Search ${title}...`}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          className={classes.searchField}
        />
      )}
      <List disablePadding className={classes.filterList}>
        {renderOptions()}
      </List>
    </Paper>
  );
};

export default HealthFilter;
